import { useMutation } from "@tanstack/react-query";
import { memoRoleSchema, type MemoRoleValue, type MemoSummary } from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  NativeSelect,
  NativeSelectOption,
} from "@portal/ui";
import { UserPlus, X } from "lucide-react";
import { useState } from "react";
import { fetchMemo, updateMemoSharing } from "../../api/client";
import { useInvalidate } from "../../api/queries";
import { MEMO_ROLE_LABELS } from "./memo-labels";
import { PersonName, ShareUserPicker, useUserName } from "./ShareUserPicker";

/**
 * 選んだメモをまとめて共有する(ユーザー要望 2026-10-15)。選んだ人を、それぞれのメモの共有に足す
 * (もう共有している人は、選んだ権限にする)。メモごとに保存するので、一部だけ失敗することがある。
 * そのときは失敗したメモを出す(成功した分はそのまま)。
 */
export function MemoBulkShareDialog({
  memos,
  ownerEmail,
  open,
  onOpenChange,
  onDone,
}: {
  memos: readonly MemoSummary[];
  ownerEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** すべて共有できたとき。 */
  onDone: () => void;
}) {
  const invalidate = useInvalidate();
  const nameOf = useUserName(open);
  const [picking, setPicking] = useState("");
  const [people, setPeople] = useState<string[]>([]);
  const [role, setRole] = useState<MemoRoleValue>("VIEWER");
  const share = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        memos.map(async (m) => {
          // 共有の中身(今の共有先)は詳細にしか無いので、メモごとに取ってから足す
          const { memo } = await fetchMemo(m.id);
          const shares = [
            ...memo.shares.filter((s) => !people.includes(s.email)),
            ...people.map((email) => ({ email, role })),
          ];
          await updateMemoSharing(m.id, {
            shares,
            orgAccess: memo.orgAccess,
            revision: memo.revision,
          });
        }),
      );
      return memos.filter((_, i) => results[i]?.status === "rejected");
    },
    onSuccess: (failed) => {
      void invalidate.memos();
      if (failed.length === 0) {
        setPeople([]);
        onDone();
      }
    },
  });
  const failed = share.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{memos.length} 件のメモを共有</DialogTitle>
          <DialogDescription>
            選んだ人を、それぞれのメモの共有に追加します(今の共有はそのまま残ります)。
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="memo-bulk-share-user">ユーザーを追加</Label>
          <div className="flex gap-2">
            <ShareUserPicker
              id="memo-bulk-share-user"
              className="min-w-0 flex-1"
              value={picking}
              onChange={(email) => {
                // 選んだらすぐ下の一覧に入れる(続けて何人でも選べる)
                if (email && !people.includes(email)) setPeople([...people, email]);
                setPicking("");
              }}
              exclude={new Set([ownerEmail, ...people])}
              enabled={open}
            />
            <div className="shrink-0">
              <NativeSelect
                aria-label="共有する権限"
                value={role}
                onChange={(e) => setRole(e.target.value as MemoRoleValue)}
              >
                {memoRoleSchema.options.map((r) => (
                  <NativeSelectOption key={r} value={r}>
                    {MEMO_ROLE_LABELS[r]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          </div>
        </div>
        {people.length > 0 && (
          <ul aria-label="共有する人" className="flex flex-col divide-y rounded-md border text-sm">
            {people.map((email) => (
              <li key={email} className="flex items-center justify-between gap-2 px-3 py-2">
                <PersonName email={email} name={nameOf(email)} />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${email} を外す`}
                  onClick={() => setPeople(people.filter((x) => x !== email))}
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {share.error && (
          <p role="alert" className="text-sm text-destructive">
            共有できませんでした。もう一度お試しください。
          </p>
        )}
        {failed.length > 0 && (
          <div role="alert" className="text-sm text-destructive">
            {memos.length - failed.length} 件は共有しました。次の {failed.length}{" "}
            件は共有できませんでした(ほかの人が同時に変えたなど)。もう一度お試しください。
            <ul className="mt-1 list-disc pl-5">
              {failed.map((m) => (
                <li key={m.id}>{m.title || "無題のメモ"}</li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            disabled={people.length === 0 || share.isPending}
            onClick={() => share.mutate()}
          >
            <UserPlus />
            共有する
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
